import React, { useState, useEffect } from 'react';
import './CropProfile.css';
import './Buttons.css';
import './Instructions.css';
import api from '../api';

const CropProfile = ({
    currentGardener,
    plants,
    userPlantData,
    onAddUserPlant,
    onDeleteUserPlant,
    onUpdateUserPlant,
    onSelectVariety,
    selectedVariety,
    onSelectCrop,
    selectedCrop,
    isDemoMode,
    currentDate,
    handleAddPlant,
    fetchCurrentGardener
}) => {

    const defaultPlantData = {
        plantID: '',
        gardenerId: currentGardener.id,
        variety: '',
        seedDepth: '',
        seedSpacing: '',
        rowSpacing: '',
        daysToGerminate: '',
        daysToMaturity: '',
        gardenType: '',
        notes: '',
        successionInterval: '',
        dontTrack: false
    };


    // --------------------------------------------------
    // State
    // --------------------------------------------------

    const [formState, setFormState] = useState(defaultPlantData);
    const [cropProfile, setCropProfile] = useState(null);
    const [plantingWindows, setPlantingWindows] = useState({});
    const [showPlantingInstructions, setShowPlantingInstructions] = useState(false);
    const [showVarieties, setShowVarieties] = useState({});
    const [foodUses, setFoodUses] = useState([]);
    const [showFoodUses, setShowFoodUses] = useState({});
    const [gardenInfo, setGardenInfo] = useState(null);

    // --------------------------------------------------
    // Reset expandable sections when changing crops
    // --------------------------------------------------

    useEffect(() => {
        setShowVarieties({});
        setShowFoodUses({});
    }, [selectedCrop]);

    // --------------------------------------------------
    // Seasons
    // --------------------------------------------------

    const seasons = [
        "Cool-Season",
        "Heat-Tolerant",
        "Cold-Hardy",
        "Overwintering"
    ];


    // --------------------------------------------------
    // Fetch selected crop profile
    // --------------------------------------------------

    useEffect(() => {

        if (!selectedCrop) {
            setCropProfile(null);
            return;
        }

        const fetchCropProfile = async () => {

            try {

                const response = await api.get(
                    `/plants/${selectedCrop}`
                );

                setCropProfile(response.data);

            } catch (err) {

                console.error(
                    "Error fetching crop profile:",
                    err
                );

                setCropProfile(null);
            }
        };

        fetchCropProfile();

    }, [selectedCrop]);

    // --------------------------------------------------
    // Fetch planting windows
    // --------------------------------------------------

    useEffect(() => {

        if (!selectedCrop) {
            setPlantingWindows({});
            return;
        }

        const fetchPlantingWindows = async () => {

            try {

                const response = await api.get(
                    `/plantingregions/${selectedCrop}`
                );
                console.log("PLANTING WINDOWS RESPONSE:", response.data);
                setPlantingWindows(response.data);

            } catch (err) {

                console.error(
                    "Error fetching planting windows:",
                    err
                );

                setPlantingWindows({});
            }
        };

        fetchPlantingWindows();

    }, [selectedCrop]);

    useEffect(() => {
        if (!cropProfile?.GardenType) {
            setGardenInfo(null);
            return;
        }

        const fetchGardenInfo = async () => {
            try {
                const response = await api.get(
                    `/gardens/${cropProfile.GardenType}`
                );

                setGardenInfo(response.data);
            } catch (err) {
                console.error('Error fetching garden information:', err);
                setGardenInfo(null);
            }
        };

        fetchGardenInfo();
    }, [cropProfile]);


    // --------------------------------------------------
    // Derived crop information
    // --------------------------------------------------

    const selectedCropName =
        cropProfile?.Name ||
        null;


    const isSeasonalCrop =
        cropProfile?.seasonalCrop === true;


    const isGrowingNow =
        Array.isArray(plants) &&
        plants.some(
            p => p.id === selectedCrop
        );


    const supportsSuccession =
        cropProfile?.successionPlantings === true;

    const isFoodUsage = cropProfile?.foodUsage === true;

    // Lisa's Favourite Variety planting methods

    const allWindows =
        Array.isArray(plantingWindows?.All?.windows)
            ? plantingWindows.All.windows
            : [];

    const hasSI = allWindows.some(window =>
        window.steps?.some(step =>
            step.action === "Start indoors"
        )
    );

    const hasDS = allWindows.some(window =>
        window.steps?.some(step =>
            step.action === "Direct sow"
        )
    );

    const siPlantingRegionIDs = allWindows
        .filter(window =>
            window.steps?.some(step =>
                step.action === "Start indoors"
            )
        )
        .map(window => window.plantingRegionID);

    const dsPlantingRegionIDs = allWindows
        .filter(window =>
            window.steps?.some(step =>
                step.action === "Direct sow"
            )
        )
        .map(window => window.plantingRegionID);
    // --------------------------------------------------
    // Fetch food uses
    // --------------------------------------------------

    useEffect(() => {

        if (!selectedCrop || !isFoodUsage) {
            setFoodUses([]);
            return;
        }

        const fetchFoodUses = async () => {

            try {

                const response = await api.get(
                    `/fooduses/${selectedCrop}`
                );

                console.log("FOOD USES RESPONSE:", response.data);

                setFoodUses(response.data);

            } catch (err) {

                console.error(
                    "Error fetching food uses:",
                    err
                );

                setFoodUses([]);
            }
        };

        fetchFoodUses();

    }, [selectedCrop, isFoodUsage]);


    // --------------------------------------------------
    // Form handling
    // --------------------------------------------------

    const handleChange = (e) => {

        const { name, value } = e.target;

        if (name === "plantID") {

            const normalizedID =
                Number(value) || null;

            setFormState({
                ...formState,
                plantID: normalizedID
            });

        } else {

            setFormState({
                ...formState,
                [name]: value
            });

        }

    };


    // --------------------------------------------------
    // Add user variety
    // --------------------------------------------------

    const handleAddUserPlant = async (e) => {

        e.preventDefault();

        const newVariety = {
            ...formState,
            plantID: selectedCrop
        };

        setFormState(newVariety);

        onAddUserPlant(newVariety);

        setFormState({
            ...defaultPlantData,
            plantID: selectedCrop,
            gardenerId: currentGardener.id
        });

    };


    // --------------------------------------------------
    // Update user variety
    // --------------------------------------------------

    const handleUpdateUserPlant = (e) => {

        e.preventDefault();

        const updated = {
            ...formState,
            plantID: selectedCrop,
            gardenerId: currentGardener.id
        };

        setFormState(updated);

        onUpdateUserPlant(updated);

    };


    // --------------------------------------------------
    // Delete user variety
    // --------------------------------------------------

    const handleDeleteUserPlant = (e) => {

        e.preventDefault();

        console.log(
            'selectedVariety',
            selectedVariety
        );

        const deleted = {
            ...formState,
            plantID: selectedCrop
        };

        setFormState(deleted);

        onDeleteUserPlant(selectedVariety);

    };

    const handleAddVariety = async (
        plantVarietyID,
        plantingRegionIDs
    ) => {
        try {

            const response = await api.post(
                '/userplants/add-variety',
                {
                    gardenerID: currentGardener.id,
                    plantID: selectedCrop,
                    plantVarietyID,
                    plantingRegionIDs
                }
            );
            await fetchCurrentGardener(isDemoMode);
            console.log(
                "ADD VARIETY RESPONSE:",
                response.data
            );

        } catch (err) {

            console.error(
                "Error adding variety:",
                err
            );

        }
    };

    // --------------------------------------------------
    // Get gardener varieties for crop
    // --------------------------------------------------

    const getVarietiesForCrop = (plantID) => {

        return Array.isArray(userPlantData)
            ? userPlantData.filter(
                data => data.plantID === plantID
            )
            : [];

    };


    // --------------------------------------------------
    // Format MM-DD dates
    // --------------------------------------------------

    const formatDate = (value) => {

        if (!value) {
            return "";
        }

        const parts = value.split("-");

        if (parts.length !== 2) {
            return value;
        }

        const [month, day] = parts;

        return new Date(
            2000,
            Number(month) - 1,
            Number(day)
        ).toLocaleDateString(
            "en-CA",
            {
                month: "short",
                day: "numeric"
            }
        );

    };


    // --------------------------------------------------
    // Get all actual planting windows
    //
    // This is especially important for NON-SEASONAL crops.
    //
    // Seasonal crops are displayed grouped by:
    // plantingConditions
    //
    // Non-seasonal crops are flattened into one list because
    // plantingCondition is not important to the gardener's view.
    // --------------------------------------------------

    // --------------------------------------------------
    // Get all actual planting windows
    //
    // NON-SEASONAL crops:
    // Use the flat "All" collection returned by the backend.
    //
    // SEASONAL crops:
    // Flatten the four planting-condition groups.
    // -------------------------------------------------- 
    const allPlantingWindows =
        Array.isArray(plantingWindows?.All?.windows)
            ? plantingWindows.All.windows
            : seasons.reduce((all, season) => {

                const seasonData = plantingWindows?.[season] || {};

                const windows =
                    Array.isArray(seasonData.windows)
                        ? seasonData.windows : [];

                return [...all, ...windows];
            }, []);

    // --------------------------------------------------
    // Count actual planting windows
    // --------------------------------------------------

    const plantingWindowCount =
        allPlantingWindows.length;


    // --------------------------------------------------
    // Planting method information
    // --------------------------------------------------

    const plantingTypes = [
        ...new Set(
            allPlantingWindows.flatMap(window =>
                Array.isArray(window.steps)
                    ? window.steps.map(step => step.action)
                    : []
            )
        )
    ];

    const hasStartIndoors =
        plantingTypes.includes("Start indoors");

    const hasTransplant =
        plantingTypes.includes("Transplant outdoors");

    const hasDirectSow =
        plantingTypes.includes("Direct sow");


    // --------------------------------------------------
    // Render a single planting window card
    // --------------------------------------------------

    const renderPlantingWindowCard = (window) => {

        return (

            <div
                key={window.plantingRegionID}
                className="planting-window-card"
            >

                {/* -----------------------------------------
                    BASIC WINDOW SUMMARY
                ----------------------------------------- */}

                <div className="planting-window-summary">

                    <div className="planting-window-steps">

                        {Array.isArray(window.steps) &&
                            window.steps.map(
                                (step, index) => (

                                    <div
                                        key={index}
                                        className="planting-step"
                                    >

                                        <strong>
                                            {step.action}
                                        </strong>

                                        <p>
                                            {formatDate(
                                                step.startPlant
                                            )}

                                            {" - "}

                                            {formatDate(
                                                step.endPlant
                                            )}
                                        </p>

                                    </div>

                                )
                            )}

                    </div>

                </div>


                {/* -----------------------------------------
                    MORE INFORMATION
                ----------------------------------------- */}

                {(
                    window.plantingConsideration ||
                    window.harvestStrategy
                ) && (

                        <details
                            className="planting-window-details"
                        >

                            <summary>
                                More about this planting window
                            </summary>


                            {/* Planting Consideration */}

                            {window.plantingConsideration && (

                                <div
                                    className="planting-consideration"
                                >

                                    <h5>
                                        Planting Consideration
                                    </h5>

                                    <p>
                                        {
                                            window.plantingConsideration
                                        }
                                    </p>

                                </div>

                            )}


                            {/* Harvest Strategy */}

                            {window.harvestStrategy && (

                                <div
                                    className="harvest-strategy"
                                >

                                    <h5>
                                        Harvest Strategy
                                    </h5>

                                    <p>
                                        {
                                            window.harvestStrategy
                                        }
                                    </p>

                                </div>

                            )}

                        </details>

                    )}

            </div>

        );

    };


    // --------------------------------------------------
    // Render
    // --------------------------------------------------

    return (

        <div className="crop-profile">


            {/* ==================================================
                HEADER
            ================================================== */}

            <h2 className="form-sub-heading">

                {!selectedCrop ? (

                    `${currentGardener?.name || 'Gardener'}, select a crop or add a new one to get started.`

                ) : !selectedCropName ? (

                    `No crop selected - please select a crop from your current list of crops or add a new one.`

                ) : selectedVariety?.isNewVariety ? (

                    `${selectedCropName} selected - enter a new variety below`

                ) : selectedVariety ? (

                    `${selectedCropName} - ${selectedVariety.variety} Variety: Let's get specific`

                ) : (

                    `${selectedCropName}`

                )}

            </h2>


            {/* ==================================================
                ADD TO CROP LIST
            ================================================== */}

            {selectedCrop && !isGrowingNow && (

                <button
                    className="button"
                    onClick={() =>
                        handleAddPlant({
                            plantID: selectedCrop
                        })
                    }
                    disabled={isDemoMode}
                >
                    Add to Your Crop List
                </button>

            )}


            {/* ==================================================
                CROP DESCRIPTION
            ================================================== */}

            {selectedCrop && cropProfile && (

                <section className="crop-profile-box">

                    {/* <h3>
                        Description
                    </h3> */}

                    <p>
                        {cropProfile.description ||
                            "No description available for this crop."}
                    </p>

                </section>

            )}



                    {(hasStartIndoors || hasDirectSow) && (

                        <div className="planting-method-explanation">
                            <button
                                type="button"
                                className="button"
                                onClick={() =>
                                    setShowPlantingInstructions(
                                        !showPlantingInstructions
                                    )
                                }
                            >
                                {showPlantingInstructions
                                    ? "Hide How to Plant"
                                    : "How to Plant"}
                            </button>


                            {showPlantingInstructions && (

                                <div className="planting-instructions-content">

                                    <h5>
                                        How to Plant
                                    </h5>


                            {hasStartIndoors && hasTransplant && (
                                <>
                                <p>
                                    <strong>Start indoors and transplant:</strong>{" "}
                                    Starting seeds indoors gives plants a head start
                                    while outdoor conditions are less favorable. By
                                    the time they are planted in the garden, they
                                    are already established and often better able to
                                    withstand early pest pressure. Established plants
                                    are also easier to space accurately in the garden
                                    and can give longer-season crops more time to mature.
                                </p>

                                <p>
                                    <strong>Transplant outdoors (TP):</strong>{" "}
                                    Transplanting means moving an established plant into the
                                    garden. You can transplant seedlings you started indoors,
                                    or buy young starter plants from a nursery or garden center.
                                    This can give the plant a stronger start in the garden and
                                    help it get ahead of early pest pressure.
                                </p>
                                </>
                            )}


                            {hasDirectSow && (

                                <p>
                                    <strong>Direct sow:</strong>{" "}
                                    Seeds are planted directly where they will grow, so
                                    there is no need to transplant them later. This can
                                    be a great option if you are unable to start seeds
                                    indoors, and some crops establish better when their
                                    roots are left undisturbed. Direct sowing is simple
                                    and avoids the stress of transplanting, although
                                    plants may take longer to get established.
                                </p>

                            )}

                                </div>

                            )}
                        </div>

            )}


            {/* ==================================================
                GARDEN STRATEGY
            ================================================== */}

            {selectedCrop && (

                <section className="crop-profile-box">

                    <h3>
                        Garden Strategy
                    </h3>

                    <p>
                        A successful garden is not just about
                        growing more. It is about timing your
                        harvests so your garden provides what
                        you need until the next opportunity
                        to harvest.
                    </p>

                </section>

            )}



            {/* ==================================================
                PLANTING WINDOWS
            ================================================== */}

            {selectedCrop && (

                <section className="crop-profile-box">

                    <h3>
                        Planting Windows
                    </h3>

                    <div className="planting-windows-introduction">

                        {isSeasonalCrop ? (

                            <p>
                                This is a seasonal crop with distinct planting windows
                                throughout the year. Each window may call for different
                                varieties or lead to a different harvest, depending on
                                when you plant.
                            </p>

                        ) : supportsSuccession ? (

                            <p>
                                This crop can be planted more than once during the
                                growing season. Planting in successive windows helps
                                spread your harvest so you can enjoy fresh produce
                                over a longer period rather than having everything
                                ready at once.
                            </p>

                            ) : allPlantingWindows.length > 1 ? (

                                <p>
                                This Crop has multiple planting opportunities 
                                throughout the growing season.  Each window represents 
                                a suitable time to start or plant this crop.
                                </p>
                            ):(

                            <p>
                                This crop has one main planting window. Plant it
                                during this period, then allow it to grow to harvest.
                                The planting window represents the best time to get
                                the crop established for a successful harvest.
                            </p>

                        )}

                    </div>
                    {/* ==================================================
                        NON-SEASONAL CROP

                        Do NOT show plantingConditions.

                        Just show all actual planting opportunities.
                    ================================================== */}

                    {!isSeasonalCrop && (
                        <>

                        <div className="planting-window-list">

                            {allPlantingWindows.length === 0 ? (

                                <p>
                                    No planting window for this crop.
                                </p>

                            ) : (

                                allPlantingWindows.map(
                                    renderPlantingWindowCard
                                )

                            )}

                            {/* ============================================================
                                FOOD USES
                                ============================================================ */}

                            {isFoodUsage && foodUses.length > 0 && (

                                <div className="food-uses-section">

                                    {/* ====================================================
                                        FOOD USES INTRODUCTION
                                        ==================================================== */}

                                    <div className="food-uses-divider"></div>

                                    <div className="food-uses-introduction">

                                        <h3>
                                            Varieties Based on Food Uses
                                        </h3>

                                        <p>
                                            Some crops can be grown for different purposes,
                                            and the best variety can depend on how you plan
                                            to use it. These food uses help you choose
                                            varieties suited to what you want to grow.
                                        </p>

                                    </div>


                                    {/* ====================================================
                                        FOOD USE GROUPS
                                        ==================================================== */}

                                    {foodUses.map((foodUse) => {

                                        const isOpen =
                                            showFoodUses[foodUse.foodUseID] === true;

                                        return (

                                            <div
                                                key={foodUse.foodUseID}
                                                className="planting-window-box"
                                            >

                                                {/* -----------------------------------------
                                                    FOOD USE NAME
                                                    ----------------------------------------- */}

                                                <h4>
                                                    {foodUse.name}
                                                </h4>


                                                {/* -----------------------------------------
                                                    FOOD USE CARD
                                                    ----------------------------------------- */}

                                                <div className="planting-window-card">

                                                    <div className="planting-window-summary">

                                                        <p>
                                                            {foodUse.description}
                                                        </p>

                                                    </div>


                                                    {/* -------------------------------------
                                                        RECOMMENDED VARIETIES
                                                        ------------------------------------- */}

                                                    {foodUse.varieties &&
                                                        foodUse.varieties.length > 0 && (

                                                            <div className="recommended-varieties">

                                                                <button
                                                                    type="button"
                                                                    className="button"
                                                                    onClick={() =>
                                                                        setShowFoodUses(prev => ({
                                                                            ...prev,
                                                                            [foodUse.foodUseID]:
                                                                                !isOpen
                                                                        }))
                                                                    }
                                                                >
                                                                    {isOpen
                                                                        ? "Hide Recommended Varieties"
                                                                        : "Recommended Varieties"
                                                                    }
                                                                </button>


                                                                {isOpen && (

                                                                    <ul>

                                                                        {foodUse.varieties.map(
                                                                            (variety) => (

                                                                                <li
                                                                                    key={
                                                                                        variety.PlantVarietyID
                                                                                    }
                                                                                >

                                                                                    <strong>
                                                                                        {variety.name}
                                                                                    </strong>

                                                                                    {variety.daysToMaturity && (
                                                                                        <>
                                                                                            {" "}
                                                                                            (
                                                                                            {
                                                                                                variety.daysToMaturity
                                                                                            }
                                                                                            {" days)"}
                                                                                        </>
                                                                                    )}

                                                                                    {variety.description && (
                                                                                        <>
                                                                                            {" - "}
                                                                                            {
                                                                                                variety.description
                                                                                            }
                                                                                        </>
                                                                                    )}
                                                                                    <div className="variety-actions">

                                                                                        {hasSI && (
                                                                                            <button
                                                                                                type="button"
                                                                                                className="button"
                                                                                                onClick={() =>
                                                                                                    handleAddVariety(
                                                                                                        variety.PlantVarietyID,
                                                                                                        siPlantingRegionIDs
                                                                                                    )
                                                                                                }
                                                                                            >
                                                                                                {hasDS
                                                                                                    ? "Add SI to My Garden"
                                                                                                    : "Add to My Garden"}
                                                                                            </button>
                                                                                        )}

                                                                                        {hasDS && (
                                                                                            <button
                                                                                                type="button"
                                                                                                className="button"
                                                                                                onClick={() =>
                                                                                                    handleAddVariety(
                                                                                                        variety.PlantVarietyID,
                                                                                                        dsPlantingRegionIDs
                                                                                                    )
                                                                                                }
                                                                                            >
                                                                                                {hasSI
                                                                                                    ? "Add DS to My Garden"
                                                                                                    : "Add to My Garden"}
                                                                                            </button>
                                                                                        )}

                                                                                    </div>
                                                                                </li>

                                                                            )
                                                                        )}

                                                                    </ul>

                                                                )}

                                                            </div>

                                                        )}

                                                </div>

                                            </div>

                                        );

                                    })}

                                </div>

                            )}
                        </div> {/* END planting-window-list */}

                        {/* ------------------------------------------
                        LISA'S FAVOURITE VARIETIES
                        ------------------------------------------ */}
                    {Array.isArray(plantingWindows?.All?.varieties) &&
                        plantingWindows.All.varieties.length > 0 &&

                        (<div className="recommended-varieties">

                            <button type="button"
                                className="button"
                                onClick={() =>
                                    setShowVarieties(prev => ({
                                        ...prev, nonSeasonal: !prev.nonSeasonal
                                    }))
                                }
                            >
                                {showVarieties.nonSeasonal
                                    ? "Hide Lisa's Favourite Varieties"
                                    : "Lisa's Favourite Varieties"
                                }
                            </button>

                            {showVarieties.nonSeasonal && (
                                <ul>
                                    {plantingWindows.All.varieties.map(
                                        (variety) => (
                                            <li
                                                key={variety.PlantVarietyID}
                                            >
                                                <strong> {variety.name}
                                                </strong>
                                                {variety.daysToMaturity && (
                                                    <>
                                                        {" "}
                                                        ({variety.daysToMaturity} days)
                                                    </>
                                                )}
                                                {variety.description && (
                                                    <>
                                                        {" - "}
                                                        {variety.description}
                                                    </>)}

                                                <div className="variety-actions">

                                                    {hasSI && (
                                                        <button
                                                            type="button"
                                                            className="button"
                                                            onClick={() =>
                                                                handleAddVariety(
                                                                    variety.PlantVarietyID,
                                                                    siPlantingRegionIDs
                                                                )
                                                            }
                                                        >
                                                            {hasDS ? "Add SI to My Garden" : "Add to My Garden"}
                                                        </button>
                                                    )}

                                                    {hasDS && (
                                                        <button
                                                            type="button"
                                                            className="button"
                                                            onClick={() =>
                                                                handleAddVariety(
                                                                    variety.PlantVarietyID,
                                                                    dsPlantingRegionIDs
                                                                )
                                                            }
                                                        >
                                                            {hasSI ? "Add DS to My Garden" : "Add to My Garden"}
                                                        </button>
                                                    )}

                                                </div>                                            </li>
                                        ))}
                                </ul>
                            )}
                        </div>
                        )}
                      </>
                    )}

                    {/* ==================================================
                        SEASONAL CROP

                        Seasonal crops are organized by their
                        plantingCondition.
                    ================================================== */}

                    {isSeasonalCrop && (

                        <div className="seasonal-planting-windows">

                            {seasons.map((season) => {

                                const seasonData =
                                    plantingWindows[season] || {};

                                const windows =
                                    Array.isArray(
                                        seasonData.windows
                                    )
                                        ? seasonData.windows
                                        : [];

                                const varieties =
                                    Array.isArray(
                                        seasonData.varieties
                                    )
                                        ? seasonData.varieties
                                        : [];

                                const hasSI = windows.some(window =>
                                    window.steps?.some(step =>
                                        step.action === "Start indoors"
                                    )
                                );

                                const hasDS = windows.some(window =>
                                    window.steps?.some(step =>
                                        step.action === "Direct sow"
                                    )
                                );

                                const siPlantingRegionIDs = windows
                                    .filter(window =>
                                        window.steps?.some(step =>
                                            step.action === "Start indoors"
                                        )
                                    )
                                    .map(window => window.plantingRegionID);

                                const dsPlantingRegionIDs = windows
                                    .filter(window =>
                                        window.steps?.some(step =>
                                            step.action === "Direct sow"
                                        )
                                    )
                                    .map(window => window.plantingRegionID);

                                // -----------------------------------------
                                // Don't show conditions with no windows
                                // -----------------------------------------

                                if (windows.length === 0) {
                                    return null;
                                }

                                return (

                                    <div
                                        key={season}
                                        className="planting-window-box"
                                    >

                                        {/* ---------------------------------
                                            SEASON
                                        --------------------------------- */}

                                        <h4>
                                            {season}
                                        </h4>


                                        {/* ---------------------------------
                                            WINDOWS
                                        --------------------------------- */}

                                        {windows.length === 0 ? (

                                            <p>
                                                No planting window for this crop.
                                            </p>

                                        ) : (

                                            windows.map(
                                                renderPlantingWindowCard
                                            )

                                        )}


                                        {/* ---------------------------------
                                            RECOMMENDED VARIETIES

                                            These belong to the season,
                                            NOT to each individual card.
                                        --------------------------------- */}

                                        {varieties.length > 0 && (

                                            <div
                                                className="recommended-varieties"
                                            >

                                                <button
                                                    type="button"
                                                    className="button"
                                                    onClick={() =>
                                                        setShowVarieties(prev => ({
                                                            ...prev,
                                                            [season]: !prev[season]
                                                        }))
                                                    }
                                                >
                                                    {showVarieties[season]
                                                        ? `Hide ${season} Varieties`
                                                        : `Varieties Suited to ${season} Planting`
                                                    }
                                                </button>


                                                {showVarieties[season] && (

                                                    <ul>

                                                        {varieties.map(
                                                            (variety) => (

                                                                <li
                                                                    key={
                                                                        variety.PlantVarietyID
                                                                    }
                                                                >

                                                                    <strong>
                                                                        {
                                                                            variety.name
                                                                        }
                                                                    </strong>

                                                                    {variety.daysToMaturity && (
                                                                        <>
                                                                            {" "}
                                                                            ({variety.daysToMaturity} days)
                                                                        </>
                                                                    ) }
                                                                    {variety.description && (
                                                                        <>
                                                                            {" - "}
                                                                            {variety.description}
                                                                        </>

                                                                    )}
                                                                    <div className="variety-actions">

                                                                        {hasSI && (
                                                                            <button
                                                                                type="button"
                                                                                className="button"
                                                                                onClick={() =>
                                                                                    handleAddVariety(
                                                                                        variety.PlantVarietyID,
                                                                                        siPlantingRegionIDs
                                                                                    )
                                                                                }
                                                                            >
                                                                                {hasDS ? "Add SI to My Garden" : "Add to My Garden"}
                                                                            </button>
                                                                        )}

                                                                        {hasDS && (
                                                                            <button
                                                                                type="button"
                                                                                className="button"
                                                                                onClick={() =>
                                                                                    handleAddVariety(
                                                                                        variety.PlantVarietyID,
                                                                                        dsPlantingRegionIDs
                                                                                    )
                                                                                }
                                                                            >
                                                                                {hasSI ? "Add DS to My Garden" : "Add to My Garden"}
                                                                            </button>
                                                                        )}

                                                                    </div>
                                                                </li>

                                                            )
                                                        )}

                                                    </ul>
                                                )}
                                            </div>

                                        )}

                                    </div>

                                );

                            })}

                        </div>

                    )}

                </section>

            )}

            {/* ==================================================
                GROWING REQUIREMENTS
            ================================================== */}

            {selectedCrop && (

                <section className="crop-profile-box">

                    <h3>
                        Companion Planting
                    </h3>

                    <p>
                        {cropProfile?.CompanionInfo ||
                            "Companion planting information will load here."}
                    </p>


                    <h3>
                        Sun Requirements
                    </h3>

                    <p>
                        {gardenInfo?.Type && (
                            <strong>{gardenInfo.Type}</strong>
                        )}

                        {gardenInfo?.description && (
                            <>
                                {gardenInfo.Type && " - "}
                                {gardenInfo.description}
                            </>
                        )}
                    </p>


                </section>

            )}

        </div>

    );

};

export default CropProfile;
